import React, { useEffect, useRef, useState } from "react";
import { Button } from "reactstrap";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  LuArrowRight,
  LuHash,
  LuLock,
  LuMail,
  LuShieldAlert,
  LuShieldCheck,
  LuUser,
} from "react-icons/lu";
import axios from "../../../axios";
import * as url from "../../../utils/url";
import { validateEmail, validatePhone } from "../../../utils/validations";
import SpinnerLoader from "../../../components/UI/SpinnerLoader/SpinnerLoader";
import { LibraryGateLayout, getApiErrorMessage, getKindCopy } from "./libraryShareShared";
import "./library-share.css";

// TBV-951: public page for a Library item shared with "Anyone with this
// link" (/l/:code redirects here). Like a public Trivvy's welcome step, it asks
// for the one detail the admin chose (User Must Provide), then shows the file.
// The file URL is only issued by the BE after that detail is accepted — and
// only for an item whose in-app privacy is Enterprise — and it is
// short-lived; reloading re-requests it with the remembered detail.

const PAGE_STATUS = {
  LOADING: "LOADING",
  UNAVAILABLE: "UNAVAILABLE",
  FORM: "FORM",
  CONTENT: "CONTENT",
};

const UNAVAILABLE_MESSAGE = "This link is invalid or no longer available.";

// Per "User Must Provide" choice: the header badge, the instruction wording,
// the field label(s) and the inline validation message.
const IDENTIFICATION_COPY = {
  EMAIL: {
    badge: "Email required",
    detail: "email address",
    label: "Email address",
    invalid: "Please enter a valid email address.",
  },
  EMPLOYEE_ID: {
    badge: "Employee ID required",
    detail: "employee ID",
    label: "Employee ID",
    invalid: "Please enter your employee ID.",
  },
  MOBILE_NUMBER: {
    badge: "Mobile number required",
    detail: "mobile number",
    label: "Mobile number",
    invalid: "Please enter a valid mobile number.",
  },
  FIRST_LAST_NAME: {
    badge: "Name required",
    detail: "first and last name",
    label: "Name",
    invalid: "Please enter your first and last name (at least 2 letters each).",
  },
};

// Same checks the BE applies, so a visitor is told before submitting.
const isUserDataValid = (identificationType, userData) => {
  if (identificationType === "EMAIL") {
    return Boolean(userData.email) && validateEmail(userData.email.trim());
  }
  if (identificationType === "EMPLOYEE_ID") {
    return Boolean(userData.employeeId && userData.employeeId.trim());
  }
  if (identificationType === "MOBILE_NUMBER") {
    return Boolean(userData.mobileNumber) && validatePhone(userData.mobileNumber);
  }
  if (identificationType === "FIRST_LAST_NAME") {
    return (
      Boolean(userData.firstName && userData.lastName) &&
      userData.firstName.trim().length >= 2 &&
      userData.lastName.trim().length >= 2
    );
  }
  return false;
};

// Remembered per tab, so a refresh doesn't ask again. Storage can be blocked
// (private mode, disabled site data), so every access is guarded.
const getStoredUserData = (code) => {
  try {
    const stored = window.sessionStorage.getItem(`libraryShare:${code}`);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};

const storeUserData = (code, userData) => {
  try {
    window.sessionStorage.setItem(`libraryShare:${code}`, JSON.stringify(userData));
  } catch (e) {
    // Not remembered — the visitor is just asked again on refresh.
  }
};

const clearStoredUserData = (code) => {
  try {
    window.sessionStorage.removeItem(`libraryShare:${code}`);
  } catch (e) {
    // Nothing to clear.
  }
};

// A shared webpage's URL was typed in by an admin; only http(s) is ever
// rendered as a link, so a "javascript:" URL can't run on this public page.
// A bare domain ("en.wikipedia.org") is read as https, as a browser would.
const getSafeWebUrl = (value) => {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }
  const trimmed = value.trim();
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withScheme);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed : null;
  } catch (e) {
    return null;
  }
};

const LibraryShareView = ({ match }) => {
  const code = match.params.code;
  const [pageStatus, setPageStatus] = useState(PAGE_STATUS.LOADING);
  const [shareInfo, setShareInfo] = useState(null);
  const [userData, setUserData] = useState({});
  // formError: the visitor's input (shown under the field). accessError: the
  // server refused (e.g. no permission) — shown as the callout in the card.
  const [formError, setFormError] = useState("");
  const [accessError, setAccessError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [content, setContent] = useState(null);
  const [isFileBroken, setIsFileBroken] = useState(false);
  // The company logo is optional: if it won't load, the header shows the name only.
  const [isOrgLogoBroken, setIsOrgLogoBroken] = useState(false);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const requestAccess = (details, { isAutomatic = false } = {}) => {
    setIsSubmitting(true);
    setFormError("");
    setAccessError("");
    return axios
      .post(url.accessPublicLibraryShare(code), { userData: details })
      .then((res) => {
        if (!isMountedRef.current) {
          return;
        }
        const data = res && res.data && res.data.data;
        if (!data || !data.kind) {
          throw new Error("Invalid response");
        }
        storeUserData(code, details);
        setContent(data);
        setIsFileBroken(false);
        setPageStatus(PAGE_STATUS.CONTENT);
      })
      .catch((error) => {
        console.log(error);
        if (!isMountedRef.current) {
          return;
        }
        const status = error && error.response && error.response.status;
        if (status === 404) {
          clearStoredUserData(code);
          setPageStatus(PAGE_STATUS.UNAVAILABLE);
          return;
        }
        setPageStatus(PAGE_STATUS.FORM);
        // No permission: always explained, even on an automatic retry with a
        // remembered detail — otherwise the page would just silently re-ask.
        if (status === 403) {
          clearStoredUserData(code);
          setAccessError(getApiErrorMessage(error));
          return;
        }
        // A remembered detail that no longer passes (e.g. the admin changed
        // what's required) just falls back to the form, silently.
        if (status === 400) {
          clearStoredUserData(code);
        }
        if (!isAutomatic) {
          setAccessError(getApiErrorMessage(error));
        }
      })
      .then(() => {
        if (isMountedRef.current) {
          setIsSubmitting(false);
        }
      });
  };

  useEffect(() => {
    setPageStatus(PAGE_STATUS.LOADING);
    axios
      .get(url.getPublicLibraryShare(code))
      .then((res) => {
        if (!isMountedRef.current) {
          return;
        }
        const info = res && res.data && res.data.data;
        if (!info || !info.identificationType) {
          setPageStatus(PAGE_STATUS.UNAVAILABLE);
          return;
        }
        setShareInfo(info);
        const stored = getStoredUserData(code);
        if (stored && isUserDataValid(info.identificationType, stored)) {
          setUserData(stored);
          requestAccess(stored, { isAutomatic: true });
        } else {
          setPageStatus(PAGE_STATUS.FORM);
        }
      })
      .catch((error) => {
        console.log(error);
        if (isMountedRef.current) {
          setPageStatus(PAGE_STATUS.UNAVAILABLE);
        }
      });
  }, [code]);

  const updateField = (field, value) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
    setFormError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting || !shareInfo) {
      return;
    }
    if (!isUserDataValid(shareInfo.identificationType, userData)) {
      setAccessError("");
      setFormError(IDENTIFICATION_COPY[shareInfo.identificationType].invalid);
      return;
    }
    requestAccess(userData);
  };

  const renderHeader = () => {
    const identificationCopy =
      shareInfo && pageStatus === PAGE_STATUS.FORM
        ? IDENTIFICATION_COPY[shareInfo.identificationType]
        : null;
    const kindCopy = getKindCopy(shareInfo ? shareInfo.kind : null);
    return (
      <header className="library_share_header">
        <div className="library_share_header_left">
          {/* Company logo + name, as the platform's navbar shows them. */}
          {shareInfo && shareInfo.organizationName && (
            <>
              <span className="library_share_brand">
                {shareInfo.organizationLogoUrl && !isOrgLogoBroken && (
                  <img
                    className="library_share_brand_logo"
                    src={shareInfo.organizationLogoUrl}
                    alt=""
                    onError={() => setIsOrgLogoBroken(true)}
                  />
                )}
                <span className="library_share_brand_name">{shareInfo.organizationName}</span>
              </span>
              <span className="library_share_header_divider" />
            </>
          )}
          <span className="library_share_header_label">Shared {kindCopy.noun}</span>
        </div>
        {identificationCopy && (
          <span className="library_share_requirement">
            <LuShieldCheck />
            {identificationCopy.badge}
          </span>
        )}
      </header>
    );
  };

  const renderInputWithIcon = ({ id, Icon, ...inputProps }) => (
    <div className="library_share_input_wrap">
      <Icon className="library_share_input_icon" />
      <input id={id} className="form-control library_share_input" maxLength={100} {...inputProps} />
    </div>
  );

  const renderIdentificationFields = () => {
    const type = shareInfo.identificationType;
    if (type === "FIRST_LAST_NAME") {
      return (
        <div className="library_share_name_row">
          <div className="library_share_field">
            <label htmlFor="library-share-first-name">
              First name <span className="library_share_required">*</span>
            </label>
            {renderInputWithIcon({
              id: "library-share-first-name",
              Icon: LuUser,
              value: userData.firstName || "",
              onChange: (e) => updateField("firstName", e.target.value),
              placeholder: "First name",
              autoComplete: "given-name",
            })}
          </div>
          <div className="library_share_field">
            <label htmlFor="library-share-last-name">
              Last name <span className="library_share_required">*</span>
            </label>
            {renderInputWithIcon({
              id: "library-share-last-name",
              Icon: LuUser,
              value: userData.lastName || "",
              onChange: (e) => updateField("lastName", e.target.value),
              placeholder: "Last name",
              autoComplete: "family-name",
            })}
          </div>
        </div>
      );
    }
    if (type === "MOBILE_NUMBER") {
      return (
        <div className="library_share_field">
          <label>
            Mobile number <span className="library_share_required">*</span>
          </label>
          <PhoneInputWithCountrySelect
            value={userData.mobileNumber || ""}
            onChange={(value) => updateField("mobileNumber", value || "")}
            defaultCountry="US"
            placeholder="Enter mobile number"
            autoComplete="tel"
          />
        </div>
      );
    }
    const isEmail = type === "EMAIL";
    const field = isEmail ? "email" : "employeeId";
    return (
      <div className="library_share_field">
        <label htmlFor="library-share-identifier">
          {IDENTIFICATION_COPY[type].label} <span className="library_share_required">*</span>
        </label>
        {renderInputWithIcon({
          id: "library-share-identifier",
          Icon: isEmail ? LuMail : LuHash,
          type: isEmail ? "email" : "text",
          value: userData[field] || "",
          onChange: (e) => updateField(field, e.target.value),
          placeholder: isEmail ? "you@company.com" : "e.g. EMP-1024",
          autoComplete: isEmail ? "email" : "off",
        })}
      </div>
    );
  };

  // Most sites refuse to be framed (X-Frame-Options / CSP), and a blocked
  // iframe can't be detected reliably — so a webpage is shown as a card that
  // opens it in a new tab, rather than a preview that is usually blank.
  const renderWebpageCard = () => {
    const webUrl = getSafeWebUrl(content.url);
    if (!webUrl) {
      return (
        <p className="text-muted text-center my-4">
          This webpage link can't be opened. Please ask the sender for a new link.
        </p>
      );
    }
    return (
      <div className="library_share_web_card">
        <div className="library_share_web_host">{webUrl.hostname.replace(/^www\./, "")}</div>
        <div className="library_share_web_url" title={webUrl.href}>
          {webUrl.href}
        </div>
        <a
          className="btn library_share_submit library_share_web_button"
          href={webUrl.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open webpage
        </a>
      </div>
    );
  };

  const isFramedDocument = Boolean(content) && (content.kind === "PDF" || content.kind === "TXT");

  const renderContent = () => {
    if (content.kind === "WEBPAGE") {
      return renderWebpageCard();
    }
    if (content.kind === "TEXT") {
      return <div className="library_share_text">{content.text}</div>;
    }
    if (content.kind === "IMAGE" && !isFileBroken) {
      return (
        <img
          className="library_share_image"
          src={content.url}
          alt={content.name || "Shared image"}
          onError={() => setIsFileBroken(true)}
        />
      );
    }
    // PDF and TXT both render in the browser's own viewer.
    if (isFramedDocument && !isFileBroken) {
      return (
        <iframe
          className="library_share_pdf"
          src={content.url}
          title={content.name || "Shared document"}
        />
      );
    }
    return null;
  };

  if (pageStatus === PAGE_STATUS.LOADING) {
    return (
      <div className="library_share_page library_share_page_center">
        <SpinnerLoader />
      </div>
    );
  }

  if (pageStatus === PAGE_STATUS.UNAVAILABLE) {
    return (
      <div className="library_share_page">
        {renderHeader()}
        <div className="library_share_message">
          <h5>{UNAVAILABLE_MESSAGE}</h5>
          <p className="text-muted">
            It may have been removed or is no longer shared. Please ask the sender for a new link.
          </p>
        </div>
      </div>
    );
  }

  if (pageStatus === PAGE_STATUS.FORM) {
    const kindCopy = getKindCopy(shareInfo.kind);
    const identificationCopy = IDENTIFICATION_COPY[shareInfo.identificationType];
    const KindIcon = kindCopy.Icon;
    return (
      <LibraryGateLayout
        header={renderHeader()}
        name={shareInfo.name}
        kind={shareInfo.kind}
        pageCount={shareInfo.pageCount}
      >
        <form className="library_share_gate" onSubmit={handleSubmit} noValidate>
          <div className="library_share_gate_intro">
            <span className="library_share_gate_chip">
              <LuLock />
            </span>
            <h4 className="library_share_gate_title">Access this {kindCopy.noun}</h4>
            <p className="library_share_gate_text">
              Enter your {identificationCopy.detail} to view the {kindCopy.noun} shared with you.
            </p>
            <div className="library_share_gate_file">
              <KindIcon />
              <span>{shareInfo.name}</span>
            </div>
          </div>

          <div className="library_share_gate_body">
            {accessError && (
              <div className="library_share_access_error" role="alert">
                <LuShieldAlert />
                <span>{accessError}</span>
              </div>
            )}
            {renderIdentificationFields()}
            {formError && (
              <p className="library_share_error" role="alert">
                {formError}
              </p>
            )}
            <Button type="submit" className="library_share_access_btn" disabled={isSubmitting}>
              {isSubmitting ? (
                "Checking..."
              ) : (
                <>
                  Access {kindCopy.noun} <LuArrowRight />
                </>
              )}
            </Button>
          </div>
        </form>
      </LibraryGateLayout>
    );
  }

  // CONTENT — a webpage's card carries its own button, so no header link.
  const canOpenUrl =
    Boolean(content.url) && content.kind !== "TEXT" && content.kind !== "WEBPAGE";
  return (
    <div className="library_share_page">
      {renderHeader()}
      <div className="library_share_page_content">
        <div
          className={`library_share_viewer ${
            isFramedDocument && !isFileBroken ? "is-pdf" : ""
          }`}
        >
          <div className="library_share_viewer_header">
            <h5 className="library_share_title mb-0">{content.name}</h5>
            {canOpenUrl && (
              <a
                className="library_share_open_link"
                href={content.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in new tab
              </a>
            )}
          </div>
          {renderContent()}
          {isFileBroken && (
            <div className="text-center my-4">
              <p className="text-muted">The file couldn't be loaded. The link may have expired.</p>
              <Button
                className="library_share_submit"
                disabled={isSubmitting}
                onClick={() => requestAccess(userData)}
              >
                {isSubmitting ? "Reloading..." : "Reload"}
              </Button>
            </div>
          )}
          {content.kind === "FILE" && (
            <p className="text-muted text-center my-4">
              This file can't be previewed here — use "Open in new tab" above to view it.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LibraryShareView;
